import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import useAuth from '../global/useAuth';
import usePaymentFlow from './usePaymentFlow';
import PaymentManager from '../../services/api/PaymentManager';
import { Order } from '../../services/exports/Interfaces';
import { ORDER_PAYMENT_STATUSES, PaymentGateway, PaymentMethod } from '../../services/exports/Constants';

/** The saved methods are read once more this long after the paid order, for a webhook that saves late. */
export const SAVED_PAYMENT_OPTIONS_RETRY_MS = 3_000;

/**
 * One read per visit to the payment screen. The screen remounts on its own within a visit (the app's
 * Ionic page on a method switch, a new customer session), and each remount is not a new visit.
 */
export const SAVED_PAYMENT_OPTIONS_VISIT_MS = 60_000;

/** Keyed by restaurant and customer: another customer logging in on the same tab is a new visit. */
const lastReadAt = new Map<string, number>();

/** Tests only. */
export const forgetSavedPaymentOptionsReads = () => lastReadAt.clear();

/**
 * A logged-in customer's saved methods, read again whenever the payment screen opens. They come with
 * the initial data once per visit, so a card or PayPal account saved on an earlier order of the same
 * visit (or one that replaced the card on file) would otherwise only show after a page refresh.
 */
export default function useRefreshSavedPaymentOptions(): void {
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const customerId = useSelector((state: StoreInterface) => state.profile?.profile?.id);
  const { isLoggedIn } = useAuth();
  // Stripe's only: the other gateways read them as they always did.
  const { hasOnlinePayments, onlinePaymentsGateway } = usePaymentFlow();
  const isStripeCheckout = hasOnlinePayments && onlinePaymentsGateway === PaymentGateway.Stripe;

  useEffect(() => {
    if (!isStripeCheckout || !isLoggedIn || !company?.id) {
      return;
    }

    const key = `${company.id}:${customerId ?? 'unknown'}`;

    if (Date.now() - (lastReadAt.get(key) ?? -Infinity) < SAVED_PAYMENT_OPTIONS_VISIT_MS) {
      return;
    }

    lastReadAt.set(key, Date.now());
    PaymentManager.getPaymentOptions(company.id);
  }, [isStripeCheckout, isLoggedIn, company?.id, customerId]);
}

/**
 * The same, once the order on screen is paid online at the restaurant the shop is showing: whatever
 * the method, and however the page learns it is paid (a card paid inside the order request is paid
 * before the page subscribes to Pusher, so its ORDER_PAID never arrives). Once per page, and once more
 * a few seconds later for a payment whose webhook saves the method after the page asked.
 */
export function useRefreshSavedPaymentOptionsOnPaid(): (order: Order | null | undefined) => void {
  const { isLoggedIn } = useAuth();
  const currentCompanyId = useSelector((state: StoreInterface) => state.initialData?.company?.id);
  const refreshedRef = useRef(false);
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => clearTimeout(retryRef.current), []);

  return useCallback(
    (order) => {
      if (
        !isLoggedIn ||
        refreshedRef.current ||
        order?.payment_status !== ORDER_PAYMENT_STATUSES.PAID ||
        order.payment?.payment_method_id === PaymentMethod.Cash
      ) {
        return;
      }

      const companyId = order.company_id ?? order.company?.id;

      // The saved list on hand is the current restaurant's: an order of another one (an old order
      // link) would replace it with that restaurant's methods.
      if (!companyId || companyId !== currentCompanyId) {
        return;
      }

      refreshedRef.current = true;
      PaymentManager.getPaymentOptions(companyId);
      retryRef.current = setTimeout(() => PaymentManager.getPaymentOptions(companyId), SAVED_PAYMENT_OPTIONS_RETRY_MS);
    },
    [isLoggedIn, currentCompanyId],
  );
}
